import React, { memo, useCallback, useEffect, useMemo, useRef } from "react";
import { useLocation } from "react-router-dom";
import { makeStyles } from "@material-ui/core";
import moment from "moment";
import {
  createLogEntryPayload,
  withTrackProps,
} from "@dpdgroupuk/react-event-tracker";

import Header from "../../components/header";
import Calendar from "../../components/Calendar";
import Dropdown from "../../components/Dropdown";
import AppMenu from "../AppMenu";
import { DATE_FORMAT } from "../../utils/query";
import useQueryState from "../../hooks/useQueryState";
import { APP_MENU, DATE_PICKER, HEADER } from "../../constants/analytics";
import {
  CENTRAL_SERVICES,
  DEPOT_OPERATIONS,
  DEPOT_PLANNING,
} from "../../constants/strings";
import { View } from "../../components/Base";
import RouteSearch from "../../components/header/RouteSearch";
import SearchParcel from "../../components/header/SearchParcel";
import palette from "../../components/styles/palette";
import ModalControlled from "../../components/ModalControlled";
import ParcelSearch from "../parcelSearch";
import { wrapPromise } from "../../utils/promise";
import DisasterRecovery from "../../components/header/DisasterRecovery";
import RecoveryAccess from "../recoveryAccess";
import useLocationQueryParams from "../../hooks/useLocationQueryParams";
import { ROUTES_PAGE } from "../../constants/routes";
import { useRouteListContext } from "../../hooks/RouteList";

const isPastDisable = process.env.REACT_APP_DATEPICKER_ENABLE_PAST !== "true";

const useStyles = makeStyles(({ typography }) => ({
  space: {
    flex: 1,
    maxWidth: typography.pxToRem(75),
  },
  menuSpace: {
    marginRight: typography.pxToRem(10),
  },
  divider: {
    color: palette.typography.white.solid,
  },
  disasterRecoverySpace: {
    flex: 1,
    maxWidth: typography.pxToRem(135),
  },
}));

const AppHeader = ({
  onDepotChange,
  onDateChanged,
  handleOpenCalendar,
  handleCloseCalendar,
  handleDepotValueChanged,
  handleOpenAppMenu,
  onSelectMenuItem,
  handleSignOut,
  onSelectDateOk,
  handleOnLoadMenu,
  handleOnLoadCalendar,
  handleOnLoadHeader,
  depots,
  disasterRecoveryAccess,
}) => {
  const styles = useStyles();
  const parcelSearchRef = useRef();
  const disasterRecoveryRef = useRef();
  const { pathname } = useLocation();
  const { detailVisible } = useLocationQueryParams();
  const { scrollTo } = useRouteListContext();

  const [{ depot, date }, update] = useQueryState(
    useMemo(
      () => [
        {
          name: "depot",
          decode: (value) =>
            depots.find((depot) => depot.value === value)?.value,
          encode: (value) =>
            depots.find((depot) => depot.value === value)?.value,
        },
        {
          name: "date",
          decode: (value) => {
            const returnValue = moment.utc(value, DATE_FORMAT);
            const isInvalidDate =
              !returnValue.isValid() ||
              (isPastDisable && returnValue.diff(moment.utc(), "days") < 0);

            if (isInvalidDate) {
              return null;
            }

            return returnValue;
          },
          encode: (value) => {
            return value.format(DATE_FORMAT);
          },
        },
      ],
      [depots]
    )
  );

  useEffect(() => {
    if (!depot && depots.length) {
      update({
        depot: depots[0]?.value,
      });
    }
  }, [depot, depots, update]);

  useEffect(() => {
    if (!date) {
      update(
        {
          date: moment.utc(),
        },
        { extend: false }
      );
    }
  }, [date, depot, update]);

  useEffect(() => {
    handleOnLoadHeader();
  }, [handleOnLoadHeader]);

  const handleDateChange = useCallback(
    (date) => {
      update({ date, depot }, { extend: false });
      onDateChanged(date.format(DATE_FORMAT));
      onSelectDateOk();
    },
    [update, depot, onDateChanged, onSelectDateOk]
  );
  const handleDepotChange = useCallback(
    (depot) => {
      update({ depot, date }, { extend: false });
      onDepotChange(depot);
    },
    [update, date, onDepotChange]
  );

  const onParcelSearch = useCallback(() => {
    wrapPromise(parcelSearchRef.current.showModal());
  }, []);

  const onDisasterRecoveryClick = useCallback(() => {
    wrapPromise(disasterRecoveryRef.current.showModal());
  }, []);

  const showRouteSearch = useMemo(
    () => pathname === ROUTES_PAGE && !detailVisible,
    [pathname, detailVisible]
  );
  const onSelectRoute = useCallback(
    (selectedRouteIndex) => scrollTo && scrollTo(selectedRouteIndex),
    [scrollTo]
  );
  return (
    <>
      <Header>
        <>
          <View className={styles.space} />
          {showRouteSearch && <RouteSearch onSelectRoute={onSelectRoute} />}
          <View className={styles.space} />
          <View className={styles.disasterRecoverySpace}>
            {disasterRecoveryAccess && (
              <DisasterRecovery onDisasterRecovery={onDisasterRecoveryClick} />
            )}
          </View>
          <View className={styles.space} />
          <SearchParcel onParcelSearch={onParcelSearch} />
          <View className={styles.space} />
          <View className={styles.divider}>|</View>
          <View className={styles.space} />
          <Calendar
            disablePast={isPastDisable}
            value={date?.toDate() ?? new Date()}
            onChange={handleDateChange}
            handleOpen={handleOpenCalendar}
            handleClose={handleCloseCalendar}
            handleOnLoad={handleOnLoadCalendar}
          />
          <View className={styles.space} />
          <Dropdown
            value={depot ?? null}
            items={depots}
            onChange={handleDepotChange}
            onInputBlur={handleDepotValueChanged}
          />
          <View className={styles.space} />
          <AppMenu
            handleOpen={handleOpenAppMenu}
            onSelectMenuItem={onSelectMenuItem}
            handleSignOut={handleSignOut}
            handleOnLoad={handleOnLoadMenu}
          />
          <View className={styles.menuSpace} />
        </>
      </Header>
      <ModalControlled
        Component={ParcelSearch}
        ref={parcelSearchRef}
        renderWhenOpen
      />
      <ModalControlled
        Component={RecoveryAccess}
        ref={disasterRecoveryRef}
        renderWhenOpen
      />
    </>
  );
};

export default withTrackProps({
  handleOpenCalendar: HEADER.SELECT_MENU,
  onDepotChange: (value) =>
    createLogEntryPayload(HEADER.SELECT_DEPOT, { value }),
  handleOpenAppMenu: HEADER.SELECT_MENU,
  handleDepotValueChanged: (value) =>
    createLogEntryPayload(HEADER.TYPE_DEPOT, { value }),
  handleSignOut: APP_MENU.SELECT_SIGN_OUT,
  onSelectMenuItem: (title) => {
    switch (title) {
      case DEPOT_OPERATIONS:
        return APP_MENU.SELECT_DEPOT_OPERATIONS;
      case CENTRAL_SERVICES:
        return APP_MENU.SELECT_CENTRAL_SERVICES;
      case DEPOT_PLANNING:
        return APP_MENU.SELECT_DEPOT_PLANNING;
      default:
        return "";
    }
  },
  handleCloseCalendar: DATE_PICKER.SELECT_CANCEL,
  onDateChanged: (value) =>
    createLogEntryPayload(DATE_PICKER.SELECT_DATE, { value }),
  onSelectDateOk: DATE_PICKER.SELECT_OK,
  handleOnLoadMenu: APP_MENU.LOAD,
  handleOnLoadCalendar: DATE_PICKER.LOAD,
  handleOnLoadHeader: HEADER.LOAD,
})(memo(AppHeader));
